import React, { useEffect, useRef } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import moment from 'moment';
import {
  FlexBox,
  BaseBox,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  CenterBox,
  Icons,
  PrimaryButton,
} from '@docon/docon-ui-library';
import {
  bookZeroFeeTeleConsult,
  getLatestTeleConsultPayment,
  getTeleConsultSession,
  initiateTeleConsultPayment,
  updateAppointmentState,
} from 'redux/reducer/appointment';
import { bgColor, borderColor, colors } from 'utilities/colorConfig';
import { cleverTapAnl } from 'utilities';
import { EventAppointmentConfirmed, EventAppointmentPaymentInitiate } from 'utilities/clevertapEvent';
import { eventService } from 'services';

interface IProps {
  isReschedule?: boolean;
  sessionId?: number;
  appointment: any;
  appointmentPricing?: any;
  slot: any;
  parentCallback: (x: any) => void;
}

const notes = [
  'You can cancel the appointment before 30 minutes from the appointment time & get 100% refund.',
  'In case of cancellation by doctor you can get 100% refund.',
  'You can reschedule the appointment free of cost.',
];

const SuccessNode = styled(CenterBox)`
  width: 100%;
  height: 100%;
  gap: 20px;
  place-content: center;
  text-align: center;
  color: ${colors.white};
  background-color: ${bgColor.success};

  ${Heading1} {
    font-size: 40px;
    font-weight: 600;
    line-height: 45px;
    color: inherit;
  }
`;

const Header = styled(BaseBox)`
  padding: 12px 16px;
  background-color: #dedede;
`;

const PaymentCard = styled(BaseBox)`
  padding: 10px var(--paddingLeftRight);
  border-radius: 4px;
  background-color: ${colors.white};

  ${FlexBox} {
    padding-bottom: 6px;
    justify-content: space-between;

    &:last-child {
      padding-top: 6px;
      padding-bottom: 0;
      border-top: 1px solid ${borderColor};
    }
  }
`;

const NoteList = styled(Heading4).attrs(() => ({ tagAs: 'ol' }))`
  margin-top: 10px;
  counter-reset: item;

  li {
    display: flex;
    gap: 0.5rem;
    font-size: 14px;
    line-height: 18px;
    margin-bottom: 5px;
    align-items: baseline;
    counter-increment: item;

    span { flex: 1; }

    &::before {
      content: counter(item);
      height: 20px;
      width: 20px;
      display: grid;
      place-content: center;
      border-radius: 50%;
      background-color: ${colors.white};
      border: 1px solid ${colors.primaryBlue};
    }
  }
`;

const Content = styled(BaseBox)`
  padding: 16px var(--paddingLeftRight);

  > div {
    margin-bottom: 16px;
  }
`;

const Root = styled(FlexBox)`
  flex-direction: column;

  ${() => isMobileOnly && css`
    padding: 1rem;

    ${SuccessNode} {
      ${Heading1} {
        font-size: 30px;
        line-height: 35px;
      } 
    }
  `}
`;

function AppointmentSuccess({
  isReschedule,
  appointment,
  appointmentPricing = {},
  slot,
  sessionId,
  parentCallback,
}: IProps) {

  const {
    doctorDetails: {
      firstName: dFirstName = '',
      lastName: dLastName = '',
    } = {},
    isTeleConsult,
  } = appointment || {};

  const currentTxnTokenRef = useRef(null);
  const dispatch = useDispatch();

  const {
    consultSession,
    consultPayment,
    teleConsultPricing,
    initiatedConsultPayment,
    isZeroFreeTeleAppointmentBooked,
    isInitiatingConsultPayment,
    isBookingZeroFreeTeleAppointment,
    isFetchingConsultPayment,
  } = useSelector(state => state.appointment);

  const followUpAppointment = teleConsultPricing?.tag === 'FOLLOWUP_CONSULT';
  const pricingDetails = isReschedule ? appointmentPricing : teleConsultPricing?.pricing_details;
  const doctorPrice = pricingDetails?.doctor_price || 0;
  const followupFee = pricingDetails?.followup_fee || 0;
  const totalFee: number = pricingDetails?.total || 0;

  useEffect(() => {
    if (!isReschedule && isTeleConsult) {
      dispatch(getTeleConsultSession(sessionId));
    }

    return () => {
      dispatch(updateAppointmentState({
        bookedAppointment: {},
        consultSession: {},
        consultPayment: {},
        initiatedConsultPayment: {},
        closingBookingModal: false,
        isZeroFreeTeleAppointmentBooked: false,
      }));
    };
  }, []);

  useEffect(() => {
    const isComplete = showSuccess();
    parentCallback(isComplete ? '' : 'Payment');
    if (isComplete) triggerAppointmentConfirmedEvent();

    if (isZeroFreeTeleAppointmentBooked || !isTeleConsult)
      setTimeout(() => {
        dispatch(updateAppointmentState({
          closingBookingModal: true,
        }));
      }, 2500);
  }, [isZeroFreeTeleAppointmentBooked]);

  useEffect(() => {
    switch (consultPayment?.status) {
      case 'FAILED': initiatePayment(); break;
      case 'INITIATED':
      case 'PENDING':
        initiatePayment(consultPayment?.additional_data?.txnToken);
        break;
    }
  }, [consultPayment]);

  useEffect(() => {
    if (initiatedConsultPayment?.body && initiatedConsultPayment?.show_payment_url) {
      postAndRedirect(initiatedConsultPayment.show_payment_url, {
        txnToken: currentTxnTokenRef.current || initiatedConsultPayment.body.txnToken,
      });
    }
  }, [initiatedConsultPayment]);

  function initiatePayment(currentTxnToken = null) {
    currentTxnTokenRef.current = currentTxnToken;
    eventService.startActionDuration('initiatePayment_current_time');
    dispatch(initiateTeleConsultPayment({
      amount: teleConsultPricing.pricing_details.total,
      sessionId: sessionId,
    }));
  }

  function postAndRedirect(path: string, params: any, method = 'post') {
    localStorage.setItem('appointmentTag', teleConsultPricing?.tag);
    const form = document.createElement('form');
    form.method = method;
    form.action = path;
    for (const key in params) {
      // eslint-disable-next-line no-prototype-builtins
      if (params.hasOwnProperty(key)) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
    }

    document.body.appendChild(form);
    form.submit();
  }

  function handleBookNowClick() {
    if (totalFee === 0)
      dispatch(bookZeroFeeTeleConsult(sessionId));
    else {
      if (consultSession.status === 'PAYMENT_WAITING' || consultSession.status === 'PAYMENT_INITIATED') {
        dispatch(getLatestTeleConsultPayment(sessionId));
      } else {
        initiatePayment();
      }
    }

    cleverTapAnl.event({
      event_name: 'appointment_payment_initiate',
      event: {
        page_name: 'payment_details',
        doctor_id: appointment.doctorID,
        appointment_date: moment(appointment.appointmentDate).format('YYYY-MM-DD'),
        slot_time: slot.parsedStartTime,
        slot_type: slot.teleConsult ? 'video' : 'physical',
        slot_rank: slot.rank,
        appointment_id: -1,
        teleconsult_id: sessionId || '',
        patient_id: consultSession.patient_id,
        transaction_amount: totalFee,
        banner_id: '-',
        banner_label: '-',
        is_banner_active: '-',
        followup_transaction_amount: followupFee,
        followup_available_flag: followUpAppointment,
        followup_used_flag: followUpAppointment,
      },
    } as EventAppointmentPaymentInitiate);
  }

  function triggerAppointmentConfirmedEvent() {
    cleverTapAnl.event({
      event_name: 'appointment_confirmed',
      event: {
        page_name: 'payment_details',
        slot_time: slot.parsedStartTime,
        slot_type: slot.teleConsult ? 'video' : 'physical',
        slot_rank: slot.rank,
        doctor_id: appointment.doctorID,
        appointment_id: appointment.appointmentID,
        teleconsult_id: sessionId || '',
        appointment_date: moment(appointment.appointmentDate).format('YYYY-MM-DD'),
        visit_reason: 'vaccination',
        transaction_amount: totalFee,
        banner_id: '-',
        banner_label: '-',
        is_banner_active: '-',
        followup_transaction_amount: followupFee,
        followup_available_flag: followUpAppointment,
        followup_used_flag: followUpAppointment,
      },
    } as EventAppointmentConfirmed);
  }

  function showSuccess() {
    if (isReschedule) return true;
    if (sessionId && !isZeroFreeTeleAppointmentBooked) return false;
    return true;
  }

  if (showSuccess())
    return (
      <SuccessNode>
        <Icons.SuccessIcon width={isMobileOnly ? 100 : 130} />
        <Heading1 title={`Appointment ${isReschedule ? 'rescheduled' : 'booked'} successfully!`} />
      </SuccessNode>
    );

  const speciality = consultSession?.appointment?.specialityDisplayName?.specialityDisplayName ||
    consultSession?.appointment?.speciality?.name;

  return (
    <Root>
      <Header>
        <Heading2 mb={'10px'} title={'Make Payment'} />
        <Heading3 title={`Dr. ${dFirstName} ${dLastName || ''}`} />
        <Heading4 title={speciality} />
      </Header>

      <Content>
        <PaymentCard>
          <FlexBox>
            <Heading4 tagAs='div' title={"Doctor's Fee"} />
            <Heading4 tagAs='div' title={`${doctorPrice}`} style={{ textDecoration: followUpAppointment ? 'line-through' : '' }} />
          </FlexBox>
          <FlexBox hidden={!followUpAppointment}>
            <Heading4 tagAs='div' title={'Follow-up Fee'} />
            <Heading4 tagAs='div' title={followupFee} />
          </FlexBox>
          <FlexBox>
            <Heading4 tagAs='div' title={'Total'} />
            <Heading4 tagAs='div' title={`${totalFee}`} />
          </FlexBox>
        </PaymentCard>

        <BaseBox>
          <Heading4 title={'Note:'} fontWeight={'bold'} />
          {
            totalFee === 0 ? (
              <NoteList>
                <li><span>{notes[2]}</span></li>
              </NoteList>
            ) : (

              <NoteList>
                <li hidden={!followUpAppointment}>
                  <span>
                    You have saved ₹{doctorPrice - followupFee}
                    on this consult as you are eligible for discounted follow-up fee with this doctor.
                  </span>
                </li>
                {
                  notes.map((note: string, index: number) => (
                    <li key={index}><span>{note}</span></li>
                  ))
                }
              </NoteList>
            )
          }
        </BaseBox>

        <PrimaryButton
          isBlock
          title={'Book Now'}
          icon={isInitiatingConsultPayment || isBookingZeroFreeTeleAppointment || isFetchingConsultPayment ? 'loading' : ''}
          onClick={handleBookNowClick}
        />
      </Content>
    </Root>
  );
}

export default AppointmentSuccess;